import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box } from '@mui/material';
import { getUser, refreshCdnCookies } from './services/api';
import SchoolProvider from './contexts/SchoolContext';
import Layout from './components/Layout';
import Login from './pages/Login';

const ChangePassword      = React.lazy(() => import('./pages/ChangePassword'));
const AdminDashboard      = React.lazy(() => import('./pages/AdminDashboard'));
const SchoolAdminDashboard = React.lazy(() => import('./pages/SchoolAdminDashboard'));
const TeacherDashboard    = React.lazy(() => import('./pages/TeacherDashboard'));
const StudentDashboard    = React.lazy(() => import('./pages/StudentDashboard'));
const CurriculumView      = React.lazy(() => import('./pages/CurriculumView'));
const CurriculumEditor    = React.lazy(() => import('./pages/CurriculumEditor'));
const FAQ                 = React.lazy(() => import('./pages/FAQ'));
const Support             = React.lazy(() => import('./pages/Support'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <CircularProgress color="primary" />
  </Box>
);

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#ec008c' },
    background: { default: '#000000', paper: '#2a2a2a' },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    h1: { fontFamily: '"Bebas Neue", Arial, sans-serif', fontSize: '4rem' },
    h2: { fontFamily: '"Bebas Neue", Arial, sans-serif', fontSize: '3.25rem' },
    h3: { fontFamily: '"Bebas Neue", Arial, sans-serif', fontSize: '2.75rem' },
    h4: { fontFamily: '"Bebas Neue", Arial, sans-serif', fontSize: '2.5rem' },
    h5: { fontFamily: '"Bebas Neue", Arial, sans-serif', fontSize: '2rem' },
    h6: { fontFamily: '"Bebas Neue", Arial, sans-serif', fontSize: '1.5rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#000000',
          backgroundImage: 'url(/assets/bg-tile.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: '20px',
          backgroundBlendMode: 'luminosity',
          opacity: 1,
          '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            backgroundImage: 'url(/assets/bg-tile.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: '20px',
            opacity: 0.1,
            pointerEvents: 'none',
            zIndex: -1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { backgroundColor: '#2a2a2a' },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: { backgroundColor: '#2a2a2a' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': { backgroundColor: '#2a2a2a', color: 'rgba(255,255,255,0.3)' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': { backgroundColor: 'rgba(236,0,140,0.2)', color: 'rgba(255,255,255,0.3)' },
        },
      },
    },
  },
});

const ProtectedRoute = ({ children, roles }) => {
  const user = getUser();
  useEffect(() => { if (user) refreshCdnCookies(); }, [user]);
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

const Home = () => {
  const user = getUser();
  if (!user) return <Navigate to="/login" />;
  const routes = { admin: '/admin', school_admin: '/school-admin', teacher: '/teacher', student: '/student' };
  return <Navigate to={routes[user.role] || '/login'} />;
};

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SchoolProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Home />} />
          <Route path="/change-password" element={<ProtectedRoute><Layout><ChangePassword /></Layout></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={['admin']}><Layout><AdminDashboard /></Layout></ProtectedRoute>} />
          <Route path="/school-admin" element={<ProtectedRoute roles={['school_admin']}><Layout><SchoolAdminDashboard /></Layout></ProtectedRoute>} />
          <Route path="/admin/curriculum/new" element={<ProtectedRoute roles={['admin']}><Layout><CurriculumEditor /></Layout></ProtectedRoute>} />
          <Route path="/admin/curriculum/:id/edit" element={<ProtectedRoute roles={['admin']}><Layout><CurriculumEditor /></Layout></ProtectedRoute>} />
          <Route path="/teacher" element={<ProtectedRoute roles={['teacher']}><Layout><TeacherDashboard /></Layout></ProtectedRoute>} />
          <Route path="/student" element={<ProtectedRoute roles={['student']}><Layout><StudentDashboard /></Layout></ProtectedRoute>} />
          <Route path="/curriculum/:id" element={<ProtectedRoute roles={['student', 'teacher', 'admin']}><Layout><CurriculumView /></Layout></ProtectedRoute>} />
          <Route path="/faq" element={<ProtectedRoute><Layout><FAQ /></Layout></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><Layout><Support /></Layout></ProtectedRoute>} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </SchoolProvider>
    </ThemeProvider>
  );
}
